import { ActivityDTO } from '@behavior/activity/dto/ActivityDTO';
import { Mode } from '@src/constants';
import { ActivityState } from './types/ActivityStates';
import { ActivityLocationDTO } from '@behavior/activity/dto/ActivityLocationDTO';
import { UseMutateFunction } from 'react-query';
import { RiskAssessmentDTO } from '@behavior/activity/dto/RiskAssessmentDTO';
import { RiskAssessmentState } from './types/RiskAssessmentState';
import { ActionDTO } from '@behavior/activity/dto/ActionDTO';
import { ActivityTraceabilityDTO } from '@behavior/activity/dto/ActivityTraceabilityDTO';
import {
  PrepareAndSendActivityDTOParams,
  PrepareAndSendCloneActivityDTOParams,
  PrepareAndSendRiskAssessmentDTOParams,
  PrepareAndSendTraceabilityFormParams,
} from './types/ActivityUtilParams';
import { ActionState } from './types/ActionState';
import { TraceabilityState } from './types/TraceabilityState';
import { ActivityCloneDTO } from '@behavior/activity/dto/ActivityCloneDTO';
import { LocationDTO } from '@behavior/inventories/dto/LocationDTO';
import { ActivityStatus } from './types/ActivityStatus';

export function prepareAndSendActivityDTO({
  activityState,
  postActivity,
}: PrepareAndSendActivityDTOParams) {
  const updatedActivity = getUpdatedActivity(activityState);
  activityState.setActivity(updatedActivity);
  postActivity(updatedActivity);
}

function getUpdatedActivity(activityState: ActivityState) {
  const {
    activity,
    title,
    chemicalProductsList,
    selectedContacts,
    outOfBuilding,
    text,
    department,
    selectedLocations,
  } = activityState;

  const updatedActivity: ActivityDTO = {
    status: ActivityStatus.CREATION,
    ...activity,
    title,
    chemicalProducts: chemicalProductsList,
    contacts: selectedContacts,
    outOfBuilding,
    description: text,
    department,
  };

  if (selectedLocations.length > 0) {
    if (selectedLocations.some((location: LocationDTO) => location)) {
      const locations = selectedLocations
        .filter((location: LocationDTO) => location.id)
        .map((location: LocationDTO) => ({ location: location } as ActivityLocationDTO));

      updatedActivity.location = locations;
    } else {
      const newItems = selectedLocations.map(
        (selectedLocation: LocationDTO) =>
          ({
            location: selectedLocation,
          } as ActivityLocationDTO)
      );

      updatedActivity.location = [...newItems];
    }
  }

  return updatedActivity;
}

export function prepareAndSendRiskAssessmentDTO({
  riskAssessmentState,
  postRiskAssessment,
  activity,
  actions,
  activityTraceabilities,
  riskAssessment,
  mode,
  riskAssessmentInitial,
}: PrepareAndSendRiskAssessmentDTOParams) {
  const riskAssessmentContainer = riskAssessmentState?.riskAssessmentContainer;
  const exposedPersonnel = riskAssessmentState?.exposedPersonnel;
  const setRiskAssessment = riskAssessmentState?.setRiskAssessment;
  const updatedActions =
    actions?.map(action => ({
      ...action,
      riskAssessmentId: riskAssessment?.id || riskAssessment?.riskAssessmentInitial?.id,
    })) || [];
  const updatedTraceability = {
    ...activityTraceabilities,
    riskAssessmentId: riskAssessment?.id || riskAssessment?.riskAssessmentInitial?.id,
  };

  if (mode == Mode.CREATE) {
    const updatedRiskAssessment: RiskAssessmentDTO = {
      ...(riskAssessmentInitial?.riskAssessmentInitial || {}),
      activity,
      riskAssessmentContainer,
      riskAssessmentExposedPersonnel: exposedPersonnel,
      actions: [],
    };

    setRiskAssessment(updatedRiskAssessment);
    postRiskAssessment(updatedRiskAssessment);
  } else if (mode == Mode.EDIT && riskAssessment && riskAssessment.riskAssessmentInitial) {
    const { riskAssessmentInitial } = riskAssessment;
    const updatedRiskAssessment: RiskAssessmentDTO = {
      ...riskAssessmentInitial,
      activity,
      riskAssessmentContainer,
      riskAssessmentExposedPersonnel: exposedPersonnel,
      actions: updatedActions,
      activityTraceabilities: updatedTraceability,
    };
    setRiskAssessment(updatedRiskAssessment);
    postRiskAssessment(updatedRiskAssessment);
  } else if (mode == Mode.EDIT && riskAssessment && !riskAssessment.riskAssessmentInitial) {
    const updatedRiskAssessment: RiskAssessmentDTO = {
      ...riskAssessment,
      activity,
      riskAssessmentContainer,
      riskAssessmentExposedPersonnel: exposedPersonnel,
      actions: updatedActions,
      activityTraceabilities: updatedTraceability,
    };
    setRiskAssessment(updatedRiskAssessment);
    postRiskAssessment(updatedRiskAssessment);
  }
}

export function prepareAndSendActionList(
  actionCardContentList: ActionDTO[],
  postActionList: UseMutateFunction<void, unknown, ActionDTO[], unknown>,
  riskAssessment?: RiskAssessmentDTO
) {
  if (actionCardContentList.length > 0) {
    const actionList = actionCardContentList.map(action => {
      const updatedAction = {
        ...action,
        riskAssessmentId: riskAssessment?.id,
      };
      return updatedAction;
    });

    postActionList(actionList);
  }
}

export function prepareAndSendTraceabilityForm({
  traceability,
  hseUnit,
  signatureDate,
  riskAssessment,
  distributionList,
  assessorList,
  setTraceability,
  postTraceability,
}: PrepareAndSendTraceabilityFormParams) {
  const updatedTraceability = {
    ...traceability,
    hseUnit: hseUnit,
    signatureDte: signatureDate,
    riskAssessment,
    distributionList: distributionList,
    traceabilityAssessor: assessorList,
    riskAssessmentId: riskAssessment?.id,
  };
  setTraceability(updatedTraceability);
  postTraceability(updatedTraceability);
}

export function updateActivityStateFromActivity(
  activity: ActivityDTO,
  activityState: ActivityState,
  mode: Mode
) {
  activityState.setActivity(activity);
  activityState.setTitle(activity.title);
  activityState.setText(activity.description);
  activityState.setSelectedContacts(activity.contacts);
  activityState.setDepartment(activity.department);

  const locations =
    activity.location?.map((location: ActivityLocationDTO) => location.location) || [];
  activityState.setSelectedLocations(locations);
  activityState.setOutOfBuilding(activity.outOfBuilding);

  if (mode === Mode.EDIT) {
    activityState.setChemicalProductsList([]);
  } else {
    activityState.setChemicalProductsList(activity.chemicalProducts);
  }

  const cardsList = [...Array.from(Array(activity.chemicalProducts.length).keys())];
  activityState.setChemicalCards(cardsList);
  const productIdList = activity.chemicalProducts.map(product => product.chemicalProduct.id);
  activityState.setChemicalProductIdList(productIdList);
  activityState.setCount(activity.chemicalProducts.length);
}

export function updateRiskAssessmentStateFromRisk(
  riskAssessment: RiskAssessmentDTO,
  riskAssessmentState: RiskAssessmentState
) {
  riskAssessmentState.setRiskAssessment(riskAssessment);
  riskAssessmentState.setRiskAssessmentContainer(riskAssessment?.riskAssessmentContainer);
  riskAssessmentState.setExposedPersonnel(riskAssessment?.riskAssessmentExposedPersonnel);
  riskAssessmentState.setAdditionalText(riskAssessment?.riskAssessmentContainer?.additionalInf);
  riskAssessmentState.setEquipmentText(
    riskAssessment?.riskAssessmentContainer?.workEquipmentAndMaterialsInfo
  );
  riskAssessmentState.setWarningNotices(riskAssessment?.riskAssessmentContainer?.hasWarningNotices);
  riskAssessmentState.setLeakTested(riskAssessment?.riskAssessmentContainer?.leakTested);
  riskAssessmentState.setAtexZone(riskAssessment?.riskAssessmentContainer?.edmsAtexZone);
  riskAssessmentState.setEsiValue(
    riskAssessment?.riskAssessmentContainer?.electricalSafetyInspectionPerformed
  );
  riskAssessmentState.setEsiEdms(riskAssessment?.riskAssessmentContainer?.edmsESI);
  riskAssessmentState.setMitigationMeasures(
    riskAssessment?.riskAssessmentContainer?.mitigationMeasures
  );
  riskAssessmentState.setWorkerTrainingInformation(
    riskAssessment?.riskAssessmentContainer?.workerTrainingInformation
  );
  riskAssessmentState.setAffectedGroup(riskAssessment?.riskAssessmentContainer?.affectedGroup);
  riskAssessmentState.setPreventionMeasures(
    riskAssessment?.riskAssessmentContainer?.preventionMeasures
  );
  riskAssessmentState.setSourcePresent(
    riskAssessment?.riskAssessmentContainer?.sourcePresent || {}
  );
  riskAssessmentState.setIgnitionLikelihood(
    riskAssessment?.riskAssessmentContainer?.ignitionLikelihood || {}
  );
  riskAssessmentState.setOrganisationalMeasures(
    riskAssessment?.riskAssessmentContainer?.organisationalMeasures
  );
  riskAssessmentState.setFlammableCategory(
    riskAssessment?.riskAssessmentContainer?.flammableCategory
  );
  riskAssessmentState.setOtherTable(
    riskAssessment?.riskAssessmentContainer?.ignitionLikelihood?.other ? true : false
  );
  riskAssessmentState.setDisabled(
    !riskAssessment?.riskAssessmentContainer?.ignitionLikelihood?.other
  );
  //likelihood table in risk assessment form:
  riskAssessmentState.setFlamesPresent(
    riskAssessment?.riskAssessmentContainer?.sourcePresent?.flames || ''
  );
  riskAssessmentState.setHotSurfacePresent(
    riskAssessment?.riskAssessmentContainer?.sourcePresent?.hotSurface || ''
  );
  riskAssessmentState.setSparksPresent(
    riskAssessment?.riskAssessmentContainer?.sourcePresent?.sparks || ''
  );
  riskAssessmentState.setChemReactionPresent(
    riskAssessment?.riskAssessmentContainer?.sourcePresent?.chemReaction || ''
  );
  riskAssessmentState.setElectricalApparatusPresent(
    riskAssessment?.riskAssessmentContainer?.sourcePresent?.electricalApparatus || ''
  );
  riskAssessmentState.setElectricityPresent(
    riskAssessment?.riskAssessmentContainer?.sourcePresent?.electricity || ''
  );
  riskAssessmentState.setElectromagneticRadiationPresent(
    riskAssessment?.riskAssessmentContainer?.sourcePresent?.electromagneticRadiation || ''
  );
  riskAssessmentState.setIonisingRadiationPresent(
    riskAssessment?.riskAssessmentContainer?.sourcePresent?.ionisingRadiation || ''
  );
  riskAssessmentState.setOtherPresent(
    riskAssessment?.riskAssessmentContainer?.sourcePresent?.otherPresent || ''
  );

  riskAssessmentState.setFlamesIgnition(
    riskAssessment?.riskAssessmentContainer?.ignitionLikelihood?.flames || ''
  );
  riskAssessmentState.setHotSurfaceIgnition(
    riskAssessment?.riskAssessmentContainer?.ignitionLikelihood?.hotSurface || ''
  );
  riskAssessmentState.setSparksIgnition(
    riskAssessment?.riskAssessmentContainer?.ignitionLikelihood?.sparks || ''
  );
  riskAssessmentState.setChemReactionIgnition(
    riskAssessment?.riskAssessmentContainer?.ignitionLikelihood?.chemReaction || ''
  );
  riskAssessmentState.setElectricalApparatusIgnition(
    riskAssessment?.riskAssessmentContainer?.ignitionLikelihood?.electricalApparatus || ''
  );
  riskAssessmentState.setElectricityIgnition(
    riskAssessment?.riskAssessmentContainer?.ignitionLikelihood?.electricity || ''
  );
  riskAssessmentState.setElectromagneticRadiationIgnition(
    riskAssessment?.riskAssessmentContainer?.ignitionLikelihood?.electromagneticRadiation || ''
  );
  riskAssessmentState.setIonisingRadiationIgnition(
    riskAssessment?.riskAssessmentContainer?.ignitionLikelihood?.ionisingRadiation || ''
  );
  riskAssessmentState.setOtherIgnition(
    riskAssessment?.riskAssessmentContainer?.ignitionLikelihood?.otherIgnition || ''
  );
  riskAssessmentState.setOtherSource(
    riskAssessment?.riskAssessmentContainer?.ignitionLikelihood?.otherTxt || ''
  );
}

export function updateActionStateFromAction(actions: ActionDTO[], actionState: ActionState) {
  actionState.setActionCardContentList(actions);
  actionState.setCount(actions.length);
}

export function updateTraceabilityStateFromTraceability(
  traceability: ActivityTraceabilityDTO,
  traceabilityState: TraceabilityState
) {
  traceabilityState.setHseUnit(traceability.hseUnit);
  traceabilityState.setAssessorList(traceability.traceabilityAssessor || []);
  traceabilityState.setDistributionList(traceability.distributionList || []);
  traceabilityState.setSignatureDate(traceability.signatureDte);
}

export function prepareAndSendCloneActivityDTO({
  activityState,
  riskAssessmentState,
  activityTraceabilities,
  actions,
  postCloneActivity,
  setIsLoading,
  traceabilityState,
}: PrepareAndSendCloneActivityDTOParams) {
  const riskAssessmentContainer = riskAssessmentState?.riskAssessmentContainer;
  const exposedPersonnel = riskAssessmentState?.exposedPersonnel;
  const updatedActivity = getUpdatedActivity(activityState);

  const updatedRiskAssessment: RiskAssessmentDTO = {
    activity: updatedActivity,
    riskAssessmentContainer: riskAssessmentContainer,
    riskAssessmentExposedPersonnel: exposedPersonnel || [],
    actions,
  };

  const updatedTraceability: ActivityTraceabilityDTO = {
    ...activityTraceabilities,
    hseUnit: traceabilityState?.hseUnit,
    signatureDte: traceabilityState?.signatureDate,
    riskAssessment: updatedRiskAssessment,
    distributionList: traceabilityState?.distributionList,
    traceabilityAssessor: traceabilityState?.assessorList,
  };

  const activityClone: ActivityCloneDTO = {
    activity: updatedActivity,
    riskAssessment: updatedRiskAssessment,
    activityTraceability: updatedTraceability,
    actions,
  };

  postCloneActivity(activityClone).then(() => {
    setIsLoading(false);
  });
}
